/**
 * Editorial photography for the public pages.
 *
 * THIS IS DESIGN CONTENT, NOT VENUE DATA. Every venue the site sells — its
 * name, photos, price, capacity, rating and city — comes from the API, and
 * nothing here is ever shown as if it were a listing. These are the campaign
 * photographs behind headings: the hero, the occasion tiles, the bands and the
 * articles. It is the one place to swap in Aghosha's own photography.
 *
 * Unsplash (free licence), requested through its CDN so each use asks for the
 * width it needs; `auto=format` serves WebP/AVIF where the browser takes it.
 * The server's CSP allows `img-src https:`. Every id was checked against the
 * CDN and its subject confirmed by eye on 2026-10-07.
 */

export const PHOTO = {
  // grand interiors
  ballroomGrand: '1549895058-36748fa6c6a7',
  banquetFloor: '1568530873454-e4103a0b3c71',
  theatreTiers: '1560184611-ff3e53f00e8f',
  auditoriumRed: '1632012773667-b68d7bd59cc4',
  auditoriumTiered: '1526045004414-3e7ed02f9ca1',
  conventionHall: '1511578314322-379afb476865',
  banquetRounds: '1624763149686-1893acf73092',
  seminarChairs: '1554752191-343d87d6c28f',
  boardroom: '1431540015161-0bf868a2d407',
  meetingRoom: '1571624436279-b272aff752b5',
  boardroomCity: '1517502884422-41eaead166d4',
  // weddings
  weddingAisle: '1523438885200-e635ba2c371e',
  weddingConfetti: '1583939003579-730e3918a45a',
  weddingCouple: '1604017011826-d3b4c23f8914',
  weddingBouquet: '1519741497674-611481863552',
  engagementRings: '1515934751635-c81c6bc9a2d8',
  receptionTable: '1464366400600-7168b8af9bc3',
  tableStyling: '1522413452208-996ff3f3e740',
  diningFlorals: '1511795409834-ef04bbd61622',
  // stage, culture, music
  stageConfetti: '1492684223066-81342ee5ff30',
  crowdSpotlight: '1505236858219-8359eb29e329',
  concertStage: '1533174072545-7a4b6ad7a6c3',
  crowdLights: '1540039155733-5bb30b53aa14',
  // conferences, seminars, corporate
  conferenceStage: '1505373877841-8d25f7d46678',
  seminarSpeaker: '1515187029135-18ee286d815b',
  workshopRoom: '1556761175-5973dc0f32e7',
  corporateMeeting: '1542744173-8e7e53415bb0',
  launchCrowd: '1560439514-4e9645039924',
  teamWorkshop: '1552581234-26160f608093',
  planningNotes: '1517048676732-d65bc937f952',
  meetingPendants: '1521737604893-d14cc237f11d',
  // celebrations
  balloons: '1530103862676-de8c9debad1d',
  stringLights: '1517457373958-b7bdd4587205',
  toast: '1519671482749-fd09be7ccebf',
  communalDining: '1528605248644-14dd04022da1',
  // catering
  platedService: '1414235077428-338989a2e8c0',
  kitchenPrep: '1551218808-94e220e084d2',
} as const;

export type PhotoKey = keyof typeof PHOTO;

/** A CDN URL at the width a layout needs. */
export function img(key: PhotoKey, width = 1200, opts: { h?: number; q?: number } = {}): string {
  const { h, q = 70 } = opts;
  return `https://images.unsplash.com/photo-${PHOTO[key]}?auto=format&fit=crop&w=${width}${h ? `&h=${h}` : ''}&q=${q}`;
}

/** A `srcset` across the widths a responsive image is served at. */
export function imgSet(key: PhotoKey, widths: number[] = [640, 1100, 1800]): string {
  return widths.map((w) => `${img(key, w)} ${w}w`).join(', ');
}
